/**
 * B2M Web SDK - Simple event tracking for web applications
 * Only tracks custom events, no platform interceptors
 */
import Config from './core/config';
import Storage from './core/storage';
import UserID from './core/user-id';
import B2MCustomTracker from './trackers/b2m';
import BulkAPI from './api/bulk-api';
import CustomAPI from './api/custom-api';
import ExperimentAPI from './api/experiment-api';
import ExperimentManager from './core/experiments';
import SessionReplayLoader from './session-replay/session-replay-loader';
import BrowserInfo from './utils/browser-info';
import { Logger } from './utils/logger';
import EventUtils from './utils/event-utils';
import { resolveCookieDomain } from './utils/cookie-utils';

class B2MWebSDK {
  constructor() {
    this.config = new Config();
    this.storage = new Storage();
    this.userId = new UserID(this.storage);
    this.logger = new Logger();
    this.browserInfo = new BrowserInfo();
    this.eventUtils = new EventUtils();
    this.bulkApi = new BulkAPI(this);
    this.customApi = new CustomAPI(this);
    this.experimentApi = new ExperimentAPI(this);
    this.experiments = new ExperimentManager(this);
    this.sessionReplay = new SessionReplayLoader(this);

    this.isInitialized = false;
    this.isOnline = navigator.onLine;
    this.retryCount = 0;
    
    // Initialize our dedicated B2M tracker (no interceptors)
    this.b2mTracker = new B2MCustomTracker(this);
  }
  
  async init(userConfig) {
    if (this.isInitialized) {
      this.logger.log("Web SDK already initialized");
      return;
    }
    
    // Configure the tracker
    this.config.init(userConfig);
    this.logger.setDebug(this.config.debug);

    // Cross-subdomain attribution/session sharing. Resolves 'auto' (default) to
    // the registrable domain so storage mirrors attribution + session to a
    // parent-domain cookie and sibling subdomains inherit them. Resolves to null
    // on localhost/IPs or when explicitly disabled.
    this.storage.cookieDomain = resolveCookieDomain(this.config.cookieDomain);
    if (this.storage.cookieDomain) {
      this.logger.log(`[B2M SDK] Cross-subdomain cookie domain: ${this.storage.cookieDomain}`);
    }

    // Cross-domain storage is now active — adopt the shared/persisted user id
    // (e.g. one the apex domain already wrote to the .parent cookie) BEFORE the
    // early tracker fires its first events, so they carry the correct user_id
    // instead of a throwaway temporary one.
    this.userId.adoptStoredId();

    // Mirror already-stored attribution/identity to the parent-domain cookie on
    // every load — not only when a value is first written. Without this a
    // RETURNING user whose localStorage is populated but whose cookies are empty
    // (cookies expired, cleared, or written by an older SDK version that didn't
    // use cookies) would never get the cross-domain cookie repopulated.
    this.storage.reMirrorToCookies();
    
    // Validate configuration
    if (!this.config.validate()) {
      this.logger.error("Missing required configuration parameters");
      return;
    }
    
    // Set up network event listeners FIRST (before async operations)
    this._setupEventListeners();
    
    // Set up B2M tracker IMMEDIATELY with temporary ID
    // This ensures we capture pageview even before fingerprinting completes
    this._setupTrackerEarly();
    
    // Initialize user ID (async due to fingerprinting) - runs in background
    this.userId.init().then(() => {
      this.logger.log("User fingerprint ready:", this.userId.getId());

      // Flush any events held while the user id was still a temp value — they
      // now resolve to the real fingerprint id at send time.
      this.sendEvents();

      // Auto-start session replay after user ID is ready so SR gets the correct ID
      if (this.config.enableSessionReplay) {
        this.sessionReplay.load().then(loaded => {
          if (loaded) this.sessionReplay.start();
        });
      }
    }).catch((e) => {
      // Fingerprint failed — don't strand the held events; release the hold and
      // flush with whatever id we have so data isn't lost.
      this.logger.error("User id init failed, flushing held events:", e);
      this.userId.idIsTemporary = false;
      this.sendEvents();
    });
    
    // Start automatic sending interval
    this._startSendingInterval();

    this.isInitialized = true;
    this.logger.log("B2M Web SDK initialized with configuration:", this.config.getAll());
    
    // Log iframe and storage mode info
    if (this.config.iframeMode) {
      this.logger.log("Running in iframe mode - optimized settings applied");
      if (this.storage.isUsingMemoryFallback()) {
        this.logger.log("localStorage unavailable (3rd-party context) - using in-memory storage");
      }
    }
  }
  
  /**
   * Get the current user's fingerprint.
   * Because the fingerprint is derived from device-stable signals, init()
   * regenerates the same id even after localStorage is cleared, so a
   * returning user is restored automatically without an extra reconciliation step.
   */
  getUserFingerprint() {
    return this.userId.getFingerprintId();
  }

  _setupEventListeners() {
    // Refresh the parent-domain cookies synchronously right before the page goes
    // away — this is the apex→subdomain redirect moment, so the session/
    // attribution cookies must be present for the subdomain to inherit them
    // (even if an external script reaped them mid-visit). document.cookie writes
    // are synchronous, so they land before the navigation completes.
    const refreshCookies = () => this.storage.refreshCookiesIfMissing();

    // Send events before page unload (fallback for older browsers)
    window.addEventListener("beforeunload", () => { refreshCookies(); this.sendEvents(true); });

    // pagehide is more reliable than beforeunload in iframe contexts
    // and on mobile browsers
    window.addEventListener("pagehide", () => { refreshCookies(); this.sendEvents(true); });

    // Send events when page becomes hidden (tab switch, minimize, etc.)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.storage.refreshCookiesIfMissing();
        this.sendEvents(true);
      }
    });
    
    // Monitor online status
    window.addEventListener("online", () => {
      this.isOnline = true;
      this.logger.log("Device came online, attempting to send queued events");
      this.sendEvents();
    });
    
    window.addEventListener("offline", () => {
      this.isOnline = false;
      this.logger.log("Device went offline, events will be queued");
    });
  }
  
  _setupTracker() {
    if (this.config.respectDoNotTrack && navigator.doNotTrack === "1") {
      this.logger.log("Respecting Do Not Track - tracking disabled");
      return;
    }
    
    // Set up the B2M tracker for custom events
    this.b2mTracker.setup();
  }
  
  /**
   * Setup tracker early without waiting for fingerprint
   * This ensures we capture the first pageview immediately
   */
  _setupTrackerEarly() {
    // Skip DoNotTrack check for now - most users don't have it enabled
    // and we want to capture pageview immediately
    if (this.config.respectDoNotTrack && navigator.doNotTrack === "1") {
      this.logger.log("Respecting Do Not Track - tracking disabled");
      return;
    }
    
    // Set up the B2M tracker immediately
    this.b2mTracker.setup();
  }
  
  _startSendingInterval() {
    setInterval(() => this.sendEvents(), this.config.sendInterval);
  }
  
  trackEvent(eventName, eventData) {
    if (!this.isInitialized) {
      this.logger.error("Cannot track event - Web SDK not initialized");
      return;
    }

    // Use our B2M tracker for custom events
    window.b2m.track(eventName, eventData || {});
  }

  /**
   * Set the customer-supplied user identifier (e.g. internal user PK).
   * Call this once the user is logged in. Pass null to clear.
   * Cached experiment assignments are wiped because the identity changed.
   */
  setUserId(externalId) {
    this.userId.setExternalId(externalId);
    this.experiments.clear();
    this.sessionReplay.onUserChange();
    this.logger.log('[B2M SDK] External user ID set:', externalId);
  }

  /**
   * Assign the current user to one or more experiments.
   * Returns the array of { ExperimentId, GroupId, GroupName } so the
   * caller can render the matching variant. Results are persisted in
   * localStorage so a returning user keeps the same group.
   *
   * @param {string[]} experimentIds
   * @param {Object} [properties={}] - targeting attributes
   * @param {Object} [options]
   * @param {boolean} [options.forceRefresh=false]
   * @returns {Promise<Array>}
   */
  async assignExperiment(experimentIds, properties = {}, options = {}) {
    if (!this.isInitialized) {
      this.logger.error('Cannot assign experiments - Web SDK not initialized');
      return [];
    }
    return this.experiments.assign(experimentIds, properties, options);
  }

  /**
   * Synchronous lookup against the local cache. Returns the group name
   * if the user has already been assigned, otherwise null.
   */
  getExperimentGroup(experimentId) {
    return this.experiments.getGroup(experimentId);
  }

  /**
   * Synchronous lookup that returns the full assignment object
   * ({ ExperimentId, GroupId, GroupName }) or null.
   */
  getExperimentAssignment(experimentId) {
    return this.experiments.getAssignment(experimentId);
  }

  /**
   * Manually start session replay recording.
   * If the SR script is not yet loaded, loads it first then starts.
   */
  async startSessionReplay() {
    if (!this.isInitialized) {
      this.logger.error('[B2M SessionReplay] SDK not initialized');
      return;
    }
    const loaded = await this.sessionReplay.load();
    if (loaded) this.sessionReplay.start();
  }

  /** Stop session replay recording. */
  stopSessionReplay() {
    this.sessionReplay.stop();
  }

  /** Returns true if session replay is currently recording. */
  isSessionReplayActive() {
    return this.sessionReplay.isActive();
  }
  
  storeEvent(event) {
    // Enrich event with user ID and timestamp
    event._userId = this.userId.getId();
    // Captured at store-time: events queued before fingerprint resolves
    // carry the random temporary ID and need to be stitched server-side.
    event._idIsTemporary = this.userId.isIdTemporary();
    event._timestamp = new Date().toISOString();
    
    // Route to appropriate storage based on event source
    if (event.interceptor === "b2m") {
      // Store B2M events in bulk queue
      this.storage.addToBulkQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getBulkQueueSize() >= this.config.batchSize) {
        this.logger.log("Bulk event batch size threshold reached, sending events");
        this.sendBulkEvents();
      }
    } else if (event.platform === "custom") {
      // Store legacy custom events
      this.storage.addToCustomQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getCustomQueueSize() >= this.config.batchSize) {
        this.logger.log("Custom event batch size threshold reached, sending events");
        this.sendCustomEvents();
      }
    } else {
      // Store other events in bulk queue
      this.storage.addToBulkQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getBulkQueueSize() >= this.config.batchSize) {
        this.logger.log("Bulk event batch size threshold reached, sending events");
        this.sendBulkEvents();
      }
    }
  }
  
  storeEventList(events) {
    if (Array.isArray(events)) {
      events.forEach(event => this.storeEvent(event));
    } else {
      this.storeEvent(events);
    }
  }
  
  /**
   * Acquisition attribution (UTM + referrer) as USER properties, shared by both
   * senders so every event carries where the user came from. Returns raw
   * {key,value} pairs; callers map through their own createEventProperty.
   * Enforces a 32-day window: once it's been >32 days since the last campaign /
   * external referral, attribution stops being sent.
   * @returns {Array<{key:string,value:(string|number)}>}
   */
  getAttributionUserParams() {
    const ts = this.storage.getItem('b2m_attribution_ts');
    if (ts && (Date.now() - ts) > 32 * 864e5) return []; // window expired
    const campaign = this.storage.getItem('b2m_campaign_params') || {};
    const initialUtm = this.storage.getItem('b2m_initial_utm') || {};
    const referrer = this.storage.getItem('b2m_referrer') || {};
    const initialReferrer = this.storage.getItem('b2m_initial_referrer') || {};
    const out = [];
    const add = (k, v) => { if (v != null && v !== '' && !k.startsWith('_')) out.push({ key: k, value: v }); };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id']
      .forEach(k => add(k, campaign[k]));
    Object.keys(initialUtm).forEach(k => add(k, initialUtm[k])); // initial_utm_*
    add('referrer_domain', referrer.referrer_domain);
    add('initial_referring_domain', initialReferrer.initial_referring_domain);
    return out;
  }

  /**
   * True until the real user_id is known. Used to hold events so nothing ships
   * with a throwaway temp id. The exception is page-unload (beacon) sends.
   */
  _holdForUserId(useBeacon) {
    if (useBeacon) return false;
    const hold = this.userId.isIdTemporary();
    if (hold) this.logger.log("[B2M SDK] Holding events until user id is ready");
    return hold;
  }

  /**
   * Consent-aware capture: when requireConsent is on and consent hasn't been
   * given, NOTHING is sent — not even on page unload (no consent = no transmit).
   * Events stay captured in the queue and are flushed by giveConsent().
   */
  _holdForConsent() {
    if (this.config.requireConsent && !this.b2mTracker.hasConsent()) {
      this.logger.log("[B2M SDK] Holding events until consent is given");
      return true;
    }
    return false;
  }

  /**
   * Called once consent is granted. A consent platform may have blocked
   * document.cookie before consent — so the cookieDomain probe could have
   * failed (→ null) and attribution/identity cookies may never have been
   * written. Re-resolve the domain and re-mirror the values to cookies now,
   * so cross-subdomain inheritance works for users who consent on landing.
   */
  persistConsentedState() {
    this.storage.cookieDomain = resolveCookieDomain(this.config.cookieDomain);
    this.storage.reMirrorToCookies();
    if (this.storage.cookieDomain) {
      this.logger.log(`[B2M SDK] Re-persisted cookies after consent on ${this.storage.cookieDomain}`);
    }
  }

  sendEvents(useBeacon = false) {
    if (!this.isInitialized) {
      this.logger.error("Cannot send events - Web SDK not initialized");
      return;
    }

    // Send both types of events
    this.sendBulkEvents(useBeacon);
    this.sendCustomEvents(useBeacon);
  }

  sendBulkEvents(useBeacon = false) {
    if (this._holdForConsent()) return;          // consent gate (even on unload)
    if (this._holdForUserId(useBeacon)) return;  // user-id gate (except unload)
    this.storage.refreshCookiesIfMissing();      // repopulate cookies if reaped
    this.bulkApi.sendEvents(useBeacon);
  }

  sendCustomEvents(useBeacon = false) {
    if (this._holdForConsent()) return;
    if (this._holdForUserId(useBeacon)) return;
    this.storage.refreshCookiesIfMissing();
    this.customApi.sendEvents(useBeacon);
  }
}

// Create singleton instance
const webSDK = new B2MWebSDK();

// Expose global functions (async for init due to fingerprinting)
window.b2mInit = async (config) => await webSDK.init(config);
window.b2mTrack = (eventName, eventData) => webSDK.trackEvent(eventName, eventData);
window.b2mSendEvents = () => webSDK.sendEvents();

// A/B test public API
window.b2mSetUserId = (externalId) => webSDK.setUserId(externalId);
window.b2mAssignExperiment = (ids, properties, options) =>
  webSDK.assignExperiment(ids, properties, options);
window.b2mGetExperimentGroup = (experimentId) => webSDK.getExperimentGroup(experimentId);
window.b2mGetExperimentAssignment = (experimentId) => webSDK.getExperimentAssignment(experimentId);

// Session Replay public API
window.b2mStartSessionReplay = () => webSDK.startSessionReplay();
window.b2mStopSessionReplay = () => webSDK.stopSessionReplay();
window.b2mIsSessionReplayActive = () => webSDK.isSessionReplayActive();

// Session ID getter — SR SDK calls this before each batch to get the current session ID
window.b2mGetSessionId = () => webSDK.storage.getSessionId();

// Expose SDK instance globally for debugging and access
window.b2mWebSDK = webSDK;

// Auto-initialization from script tag data attributes
window.b2mReady = (async function autoInit() {
  try {
  webSDK.logger.log('[B2M SDK] Auto-initialization started...');
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    webSDK.logger.log('[B2M SDK] Waiting for DOM...');
    await new Promise(resolve => {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
  }
  
  webSDK.logger.log('[B2M SDK] DOM ready, looking for script tag...');
  
  // Find the script tag with data-api-key
  const scriptTag = document.querySelector('script[data-api-key]');
  
  if (scriptTag && scriptTag.hasAttribute('data-api-key')) {
    webSDK.logger.log('[B2M SDK] Found script tag with data-api-key');
    
    const config = {
      apiKey: scriptTag.getAttribute('data-api-key'),
      debug: scriptTag.getAttribute('data-debug') === 'true',
      batchSize: parseInt(scriptTag.getAttribute('data-batch-size')) || 5,
      sendInterval: parseInt(scriptTag.getAttribute('data-send-interval')) || 30000,
      respectDoNotTrack: scriptTag.getAttribute('data-respect-dnt') !== 'false',
      requireConsent: scriptTag.getAttribute('data-require-consent') === 'true',
      enableSPATracking: scriptTag.getAttribute('data-spa-tracking') !== 'false',
      sessionTimeout: parseInt(scriptTag.getAttribute('data-session-timeout')) || 1800000,
      duplicatePreventionTimeout: parseInt(scriptTag.getAttribute('data-duplicate-timeout')) || 5000,
      maxRetries: parseInt(scriptTag.getAttribute('data-max-retries')) || 3,
      retryTimeout: parseInt(scriptTag.getAttribute('data-retry-timeout')) || 30000,
      enableGTMDataLayerListener: scriptTag.getAttribute('data-enable-gtm-listener') === 'true',
      enableSessionReplay: scriptTag.getAttribute('data-session-replay') === 'true',
      // script/host fall back to baked-in defaults in Config when attribute is absent
      ...(scriptTag.hasAttribute('data-session-replay-script') && {
        sessionReplayScriptUrl: scriptTag.getAttribute('data-session-replay-script')
      }),
      ...(scriptTag.hasAttribute('data-session-replay-host') && {
        sessionReplayHost: scriptTag.getAttribute('data-session-replay-host')
      }),
      sessionReplaySiteId: scriptTag.getAttribute('data-session-replay-site-id')
        ? parseInt(scriptTag.getAttribute('data-session-replay-site-id'))
        : null,
      sessionReplayUseCustomSessionId:
        scriptTag.getAttribute('data-session-replay-use-custom-session-id') === 'true',
      gtmExcludeEvents: (() => {
        if (!scriptTag.hasAttribute('data-gtm-exclude-events')) return ['page_view'];
        try {
          return JSON.parse(scriptTag.getAttribute('data-gtm-exclude-events'));
        } catch (e) {
          console.error('[B2M SDK] Error parsing data-gtm-exclude-events, using default', e);
          return ['page_view'];
        }
      })()
    };
    
    // Parse custom event library (e.g. data-custom-event-lib="pulpo")
    const customEventLib = scriptTag.getAttribute('data-custom-event-lib');
    if (customEventLib) {
      config.customEventLib = customEventLib.trim().toLowerCase();
      webSDK.logger.log(`[B2M SDK] Custom event library: ${config.customEventLib}`);
    }

    // Parse optional app identifier override (e.g. data-app-identifier="b2metric.com")
    // Boş bırakılırsa SDK çalıştığı domain'in hostname'ini otomatik kullanır.
    const appIdentifier = scriptTag.getAttribute('data-app-identifier');
    if (appIdentifier && appIdentifier.trim()) {
      config.appIdentifier = appIdentifier.trim();
      webSDK.logger.log(`[B2M SDK] App identifier override: ${config.appIdentifier}`);
    }

    // Parse optional cookie domain for cross-subdomain attribution/session sharing
    // (e.g. data-cookie-domain=".birfatura.com").
    const cookieDomain = scriptTag.getAttribute('data-cookie-domain');
    if (cookieDomain && cookieDomain.trim()) {
      config.cookieDomain = cookieDomain.trim();
      webSDK.logger.log(`[B2M SDK] Cookie domain (cross-subdomain): ${config.cookieDomain}`);
    }

    // A/B-only mode — keeps ab_test_view + user props, drops behavioural events.
    // data-ab-mode="true" → dedup behavioural data with BigQuery.
    if (scriptTag.getAttribute('data-ab-mode') === 'true') {
      config.abMode = true;
      webSDK.logger.log('[B2M SDK] abMode enabled via data attribute');
    }

    webSDK.logger.log('[B2M SDK] Config:', config);
    
    // Parse event groups
    const eventGroupsAttr = scriptTag.getAttribute('data-event-groups');
    if (eventGroupsAttr) {
      config.eventGroups = {};
      eventGroupsAttr.split(',').forEach(group => {
        config.eventGroups[group.trim()] = { enabled: true };
      });
      webSDK.logger.log('[B2M SDK] Event groups:', config.eventGroups);
    }
    
    // Parse custom events
    const customEventsAttr = scriptTag.getAttribute('data-custom-events');
    let customEvents = null;
    if (customEventsAttr) {
      try {
        customEvents = JSON.parse(customEventsAttr);
        webSDK.logger.log('[B2M SDK] Custom events:', customEvents);
      } catch (e) {
        console.error('[B2M SDK] Error parsing custom events', e);
      }
    }
    
    // Auto-initialize SDK
    webSDK.logger.log('[B2M SDK] Initializing SDK...');
    await window.b2mInit(config);
    webSDK.logger.log('[B2M SDK] SDK initialized successfully!');
    
    // Set up custom events if provided
    if (customEvents && Array.isArray(customEvents)) {
      customEvents.forEach(event => {
        const eventData = event.d || {};
        
        if (event.t === 'onload') {
          // Track on page load (immediately)
          window.b2mTrack(event.n, eventData);
        } else if (event.t === 'button' && event.s) {
          // Track on button click
          const element = document.querySelector(event.s);
          if (element) {
            element.addEventListener('click', () => {
              window.b2mTrack(event.n, eventData);
            });
          }
        } else if (event.t === 'form' && event.s) {
          // Track on form submit
          const form = document.querySelector(event.s);
          if (form) {
            form.addEventListener('submit', () => {
              window.b2mTrack(event.n, eventData);
            });
          }
        }
      });
    }
    
    return true;
  } else {
    webSDK.logger.log('[B2M SDK] No script tag found with data-api-key, waiting for manual init');
    return false;
  }
  } catch (e) {
    console.error('[B2M SDK] Auto-initialization failed:', e);
    return false;
  }
})();

export default webSDK; 